import classNames from 'classnames'
import { CSSProperties, useEffect, useMemo, useRef } from 'react'
import { Editor, Node, Element as SlateElement } from 'slate'
import { ReactEditor, RenderElementProps, useSlate } from 'slate-react'

import { updateExpandBlock } from 'core-components/library/richTextEditor/helpers/slateHelper'
import { BLOCK_FORMATS } from 'core-components/library/richTextEditor/interface/custom-types'

import styles from './ExpandBlock.module.scss'

interface IProps {
  style: CSSProperties
}

export const ExpandBlock = (props: RenderElementProps & IProps) => {
  const detailsRef = useRef<HTMLDetailsElement>(null)
  const editor = useSlate()
  const path = ReactEditor.findPath(editor, props.element)
  const firstRender = useRef(true)

  useEffect(() => {
    // When building a component, we set the type of expand block depending on the editor editing mode
    if (firstRender.current) {
      firstRender.current = false
      if (detailsRef.current) {
        detailsRef.current.open = !editor.disabled
      }
      updateExpandBlock(editor, path, !editor.disabled)
    } else {
      if (detailsRef.current) {
        detailsRef.current.open = props.element['isOpen']
      }
    }
  }, [props.element, editor, path, editor.disabled])

  const isEmpty = useMemo(() => {
    const expandBlockChildren = props.element.children
    const expandBlockContent = expandBlockChildren.filter(
      (child: Node) =>
        SlateElement.isElement(child) && child.type !== BLOCK_FORMATS.expandBlockTitle
    )

    return (
      expandBlockContent.length === 1 &&
      SlateElement.isElement(expandBlockContent[0]) &&
      Editor.isEmpty(editor, expandBlockContent[0])
    )
  }, [props.element.children])

  return (
    <details
      style={props.style}
      onBlur={(e) => {
        e.preventDefault()
        e.stopPropagation()
      }}
      ref={detailsRef}
      className={styles.root}
    >
      {props.children}
      <div
        data-testid={'expand-block-placeholder'}
        contentEditable={false}
        className={classNames(styles.placeholder, { [styles.placeholder__hidden]: !isEmpty })}
      >
        Edit the placeholder...
      </div>
    </details>
  )
}
